import AppState from '@/states/app'
import { getLocationTzTimezone } from '@/utils/timezones'
import moment from 'moment'
import { reactive } from 'vue'
import { DayAvailability, DayAvailabilityConfig } from './DayAvailability'

export const defaultRulesConfig: DayAvailabilityConfig[] = [
  {
    type: 'wday',
    day: 'sunday',
    intervals: [],
    isActive: false,
  },
  {
    type: 'wday',
    day: 'monday',
    intervals: [
      {
        from: '8:0',
        to: '17:0',
      },
    ],
    isActive: true,
  },
  {
    type: 'wday',
    day: 'tuesday',
    intervals: [
      {
        from: '8:0',
        to: '17:0',
      },
    ],
    isActive: true,
  },
  {
    type: 'wday',
    day: 'wednesday',
    intervals: [
      {
        from: '8:0',
        to: '17:0',
      },
    ],
    isActive: true,
  },
  {
    type: 'wday',
    day: 'thursday',
    intervals: [
      {
        from: '8:0',
        to: '17:0',
      },
    ],
    isActive: true,
  },
  {
    type: 'wday',
    day: 'friday',
    intervals: [
      {
        from: '8:0',
        to: '17:0',
      },
    ],
    isActive: true,
  },
  {
    type: 'wday',
    day: 'saturday',
    intervals: [],
    isActive: false,
  },
]

export const defaultRules: DayAvailability[] = defaultRulesConfig.map(
  day => new DayAvailability(day)
)
export const MAX_VISIBLE = 5
export interface ISchedule {
  id: string
  name: string
  userId: string
  calendarIds: string[]
  rules: DayAvailability[]
  timezone: string
  isDefault: boolean
  locationId: string
  deleted: boolean
  dateAdded: string
  dateUpdated: string
  isCreated: boolean
  isCustom: boolean
}

export class Schedule {
  id: string
  name: string
  userId: string
  rules: DayAvailability[] = []
  timezone: string
  isDefault: boolean
  locationId: string
  deleted: boolean
  dateAdded: string
  dateUpdated: string

  isCreated: boolean
  calendarIds: string[]
  isCustom: boolean

  static OFFICE_DAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]

  constructor(data: Partial<ISchedule>) {
    this.id = data?.id || ''
    this.name = data?.name || data?.id || ''
    this.userId = data?.userId || ''
    this.calendarIds = reactive(data?.calendarIds || [])
    this.timezone = data?.timezone || ''
    this.isDefault = data?.isDefault || false
    this.locationId = data?.locationId || ''
    this.deleted = data?.deleted || false
    this.dateAdded = data?.dateAdded || ''
    this.dateUpdated = data?.dateUpdated || ''
    this.isCreated = data?.isCreated ?? true
    this.isCustom = data?.isCustom ?? false
    this.setRulesFromScheduleData(data?.rules || [])
  }

  setRulesFromScheduleData(rules: DayAvailability[]): void {
    // If the schedule id is a Firestore id (20-char alphanumeric), always set rules from data (even if empty)
    const isFirestoreId = /^[a-zA-Z0-9]{20}$/.test(this.id)
    const hasRules = Array.isArray(rules) && rules.length > 0
    if (isFirestoreId) {
      this.rules = this.getAvailabilityDays(rules)
    } else if (hasRules) {
      this.rules = this.getAvailabilityDays(rules)
    } else {
      this.rules = defaultRules
    }
  }

  /**
   * Get the availability days for the schedule also includes the missing days
   * It will also set the isActive to false for the missing days
   * Custom availability also set in rules
   * @param rules - The rules to get the availability days from
   * @returns The availability days
   */
  getAvailabilityDays(rules: DayAvailability[]): DayAvailability[] {
    const weekdayRules = rules.filter(rule => rule.type === 'wday')
    const dateRules = rules.filter(rule => rule.type === 'date')
    const existingWeekdays = new Map(
      weekdayRules.map(rule => [rule.day?.toLowerCase(), rule])
    )
    const completeWeekdayRules = Schedule.OFFICE_DAYS.map(day => {
      const existingRule = existingWeekdays.get(day?.toLowerCase())
      if (existingRule) {
        return new DayAvailability(existingRule)
      }
      return new DayAvailability({
        day,
        type: 'wday',
        intervals: [],
        isActive: false,
      })
    })

    return [
      ...completeWeekdayRules,
      ...dateRules.map(rule => new DayAvailability(rule)),
    ]
  }

  setCalendarIds(value: string[]) {
    this.calendarIds = value
  }
  addCalendarId(value: string) {
    if (!this.calendarIds?.includes(value)) {
      this.calendarIds?.push(value)
    }
  }

  /**
   * Get the weekly days schedule
   * @returns The weekly schedule
   */
  getWeeklySchedule(): DayAvailability[] {
    return this.rules?.filter(day => day.type === 'wday') || []
  }

  /**
   * Get the date specific date/custom schedule
   * @returns The date specific hours schedule
   */
  getDateSpecificHours(): DayAvailability[] {
    return this.rules?.filter(day => day.type === 'date') || []
  }

  getTimezone(): string {
    return this.timezone
  }

  setTimezone(value: string): void {
    this.timezone = value
  }

  toggleDayEnabled(rule: DayAvailabilityConfig): void {
    rule.isActive = !rule.isActive

    if (rule.isActive) {
      rule.intervals = [
        {
          from: '8:0',
          to: '17:0',
        },
      ]
    } else {
      rule.intervals = []
    }
  }

  removeDateSpecificHours(index: number): void {
    const dateSpecificRules = this.getDateSpecificHours()
    if (index >= 0 && index < dateSpecificRules.length) {
      const ruleIndex = this.rules.findIndex(
        rule => rule === dateSpecificRules[index]
      )
      if (ruleIndex !== -1) {
        this.rules.splice(ruleIndex, 1)
      }
    }
  }

  setRules(rules: DayAvailability[]): void {
    this.rules = rules
  }

  // Time Validation Methods
  isHourDisabled(hour: number, startTime?: string, endTime?: string): boolean {
    const startHour = startTime ? moment(startTime, 'H:m').hour() : null
    const endHour = endTime ? moment(endTime, 'H:m').hour() : null
    if (hour === 0) {
      return false
    }
    if (startHour && hour < startHour) {
      return true
    }
    if (endHour && hour > endHour) {
      return true
    }
    return false
  }

  // eslint-disable-next-line max-params
  isMinuteDisabled(
    min: number,
    hour: number | null,
    startTime?: string,
    endTime?: string
  ): boolean {
    const startMin = startTime ? moment(startTime, 'H:m').minute() : null
    const startHour = startTime ? moment(startTime, 'H:m').hour() : null
    const endMin = endTime ? moment(endTime, 'H:m').minute() : null
    const endHour = endTime ? moment(endTime, 'H:m').hour() : null
    if (hour === 0) {
      return false
    }
    if (startHour && startMin && hour === startHour && min <= startMin) {
      return true
    }
    if (endHour && endMin && hour === endHour && min >= endMin) {
      return true
    }
    return false
  }

  // Copy Schedule Methods
  copyScheduleToDay(fromDay: string, toDay: string): void {
    if (fromDay === toDay) {
      return
    }

    const sourceDay = this.getWeeklySchedule().find(day => day?.day === fromDay)
    if (!sourceDay) {
      return
    }

    const targetDay = this.getWeeklySchedule().find(day => day.day === toDay)
    if (!targetDay) {
      return
    }

    // Create deep copies of intervals to ensure each day has independent interval objects
    targetDay.intervals = (sourceDay.intervals || []).map(interval => ({
      from: interval.from,
      to: interval.to,
    }))
    targetDay.isActive = sourceDay.isActive
  }

  copyScheduleToMultipleDays(fromDay: string, toDays: string[]): void {
    toDays.forEach(day => {
      if (day !== fromDay) {
        this.copyScheduleToDay(fromDay?.toLowerCase(), day?.toLowerCase())
      }
    })
  }
  isDateDisabled(ts: number) {
    const selectedDate = moment(ts).startOf('day')
    const currentDate = getLocationTzTimezone(
      AppState.locationTimezone
    ).startOf('day')

    return selectedDate.isBefore(currentDate)
  }

  setAllDayUnavailable() {
    this.rules.forEach(rule => {
      rule.intervals = []
      rule.isActive = false
    })
    return this
  }

  // Data Export/Import Methods
  toJSON() {
    const rules = this.rules
      .map(rule => {
        if (rule.type === 'date' || rule.intervals.length > 0) {
          return rule.toJSON()
        }
        return null
      })
      .filter(rule => rule !== null)
    return {
      name: this.name,
      userId: this.userId,
      calendarIds: this.calendarIds,
      rules,
      timezone: this.timezone,
      isDefault: this.isDefault,
      locationId: this.locationId,
      isCustom: this.isCustom,
    }
  }

  fromJSON(data: any) {
    this.rules = data.rules
    this.timezone = data.timezone
    this.isDefault = data.isDefault
    this.locationId = data.locationId
    this.deleted = data.deleted
    this.dateAdded = data.dateAdded
    this.dateUpdated = data.dateUpdated
    this.calendarIds = data.calendarIds
    this.id = data.id
    this.name = data.name
    this.userId = data.userId
  }
  private static areArraysEqual(a: any[], b: any[]): boolean {
    if (a.length !== b.length) {
      return false
    }
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((val, idx) => val === sortedB[idx])
  }

  private static sortRules(rules: DayAvailability[]): DayAvailability[] {
    return [...rules].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type.localeCompare(b.type)
      }
      if (a.type === 'wday') {
        return (a.day || '').localeCompare(b.day || '')
      }
      if (a.type === 'date') {
        return (a.date || '').localeCompare(b.date || '')
      }
      return 0
    })
  }

  private static normalizeRule(rule: DayAvailability): DayAvailability {
    const sortedIntervals = (rule.intervals || [])
      .map(i => ({ ...i }))
      .sort((a, b) => (a.from + a.to).localeCompare(b.from + b.to))
    return new DayAvailability({ ...rule, intervals: sortedIntervals })
  }

  private static areRulesEqualOrdered(
    a: DayAvailability[],
    b: DayAvailability[]
  ): boolean {
    const sortedA = Schedule.sortRules(a).map(Schedule.normalizeRule)
    const sortedB = Schedule.sortRules(b).map(Schedule.normalizeRule)

    if (sortedA.length !== sortedB.length) {
      return false
    }

    for (let i = 0; i < sortedA.length; i++) {
      const ruleA = sortedA[i]
      const ruleB = sortedB[i]

      if (
        ruleA.type !== ruleB.type ||
        ruleA.day !== ruleB.day ||
        ruleA.date !== ruleB.date ||
        ruleA.isActive !== ruleB.isActive ||
        ruleA.overlapIndex !== ruleB.overlapIndex ||
        JSON.stringify(ruleA.intervals) !== JSON.stringify(ruleB.intervals)
      ) {
        return false
      }
    }

    return true
  }
  equals(other: Schedule): boolean {
    // Scalar fields
    const scalarFields = ['name', 'timezone', 'isDefault', 'locationId']

    for (const field of scalarFields) {
      if ((this as any)[field] !== (other as any)[field]) {
        return false
      }
    }

    // Compare calendarIds
    if (!Schedule.areArraysEqual(this.calendarIds, other.calendarIds)) {
      return false
    }

    // Compare rules
    if (!Schedule.areRulesEqualOrdered(this.rules, other.rules)) {
      return false
    }

    return true
  }

  //backwards compatibility with open hours and availability
  dayStringToIndex(day: string): number {
    const map = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    }
    return map[day?.toLowerCase()] ?? 0
  }
  getOpenHours(): any[] {
    const grouped: Record<number, any[]> = {}

    this.rules
      .filter(rule => rule.type === 'wday')
      .forEach(rule => {
        const dayIdx = this.dayStringToIndex(rule.day || '')

        if (!grouped[dayIdx]) {
          grouped[dayIdx] = []
        }
        if (Array.isArray(rule.intervals)) {
          rule.intervals.forEach(interval => {
            grouped[dayIdx].push({
              openHour: parseInt(interval.from.split(':')[0], 10),
              openMinute: parseInt(interval.from.split(':')[1], 10),
              closeHour: parseInt(interval.to.split(':')[0], 10),
              closeMinute: parseInt(interval.to.split(':')[1], 10),
            })
          })
        }
      })

    // Build the openHours array, sorted by day index
    return Object.entries(grouped)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([dayIdx, hours]) => ({
        daysOfTheWeek: [parseInt(dayIdx, 10)],
        hours,
      }))
  }

  getCustomAvailability(calendarId: string): any[] {
    return this.rules
      .filter(rule => rule.type === 'date')
      .map(rule => ({
        date: rule.date + 'T00:00:00.000Z', // should be ISO string (append T00:00:00.000Z)
        hours: (rule.intervals || []).map(interval => ({
          openHour: parseInt(interval.from.split(':')[0], 10),
          openMinute: parseInt(interval.from.split(':')[1], 10),
          closeHour: parseInt(interval.to.split(':')[0], 10),
          closeMinute: parseInt(interval.to.split(':')[1], 10),
        })),
        ...{ calendarId: calendarId },
      }))
  }

  openHoursAndAvailabilitiesToRules(
    openHours: any[],
    availabilities: any[]
  ): any[] {
    const dayMap = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ]
    const wdayRules = (openHours || []).map(oh => {
      const dayIdx = (oh.daysOfTheWeek && oh.daysOfTheWeek[0]) || 0
      return {
        type: 'wday',
        day: dayMap[dayIdx],
        intervals: (oh.hours || []).map(h => ({
          from: `${h.openHour}:${h.openMinute}`,
          to: `${h.closeHour}:${h.closeMinute}`,
        })),
        isActive: (oh.hours || []).length > 0,
      }
    })

    const normalizeDate = (date: string) => moment(date).format('YYYY-MM-DD')

    const filteredAvailabilities = (availabilities || [])
      .filter(av => !av.deleted) // Step 1: Remove deleted
      .map(av => ({ ...av, normalizedDate: normalizeDate(av.date) }))
    // Count occurrences of each normalized date
    const dateCounts: Record<string, number> = {}
    filteredAvailabilities.forEach(av => {
      dateCounts[av.normalizedDate] = (dateCounts[av.normalizedDate] || 0) + 1
    })

    const uniqueAvailabilities = filteredAvailabilities.filter(
      av => dateCounts[av.normalizedDate] === 1
    )
    const dateRules = uniqueAvailabilities.map(av => ({
      type: 'date',
      date: av.date,
      intervals: (av.hours || []).map(h => ({
        from: `${h.openHour}:${h.openMinute}`,
        to: `${h.closeHour}:${h.closeMinute}`,
      })),
    }))
    this.rules = [...wdayRules, ...dateRules].map(
      rule => new DayAvailability(rule)
    )
    return this.rules
  }
}
